import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, Gavel } from "@phosphor-icons/react";
import { toast } from "sonner";

const AUTH_BG = "https://images.pexels.com/photos/31732696/pexels-photo-31732696.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      const to = location.state?.from || "/";
      navigate(to);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative bg-[#0B1929] overflow-hidden">
        {/* Cyan-glow radial + subtle grain so the panel reads as branded, not just dark. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(1200px 600px at 20% 30%, rgba(34,211,238,0.18), transparent 60%)," +
              "radial-gradient(900px 500px at 80% 70%, rgba(13,148,136,0.22), transparent 60%)," +
              "#0B1929",
          }}
        />
        <div className="relative h-full flex flex-col justify-between p-12 xl:p-16">
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 bg-[#22D3EE] flex items-center justify-center">
              <Gavel size={22} weight="bold" color="#0B1929" />
            </div>
            <div style={{ fontSize: "1.75rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1 }}>
              INVERSE<span className="text-[#22D3EE]">.</span>
            </div>
          </div>
          <div className="text-white max-w-xl">
            <div className="text-xs tracking-[0.35em] uppercase mb-6 text-[#22D3EE] font-semibold">Procurement, Reversed</div>
            <h1
              style={{
                fontSize: "4rem",
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
                fontWeight: 800,
              }}
            >
              Lowest price.<br />
              Blind to identity.<br />
              <span className="text-white/50">Round after round.</span>
            </h1>
            <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-md">
              Upload requirements, invite vendors, and let the market drive prices down — round after round, without ever revealing who bid what.
            </p>
            <div className="mt-10 pt-6 border-t border-white/10 text-xs text-white/60 tracking-[0.25em] uppercase">
              A Radish Technologies Platform
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0D9488] font-semibold mb-3">Authentication</div>
          <h2 className="font-display text-3xl mb-2 text-[#0B1929]">Sign in</h2>
          <p className="text-sm text-neutral-600 mb-10">Access your auctions and bid securely.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-600">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-none border-black/20 h-11 focus-visible:ring-2 focus-visible:ring-[#22D3EE] focus-visible:ring-offset-1 focus-visible:border-[#22D3EE]"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-600">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-none border-black/20 h-11 focus-visible:ring-2 focus-visible:ring-[#22D3EE] focus-visible:ring-offset-1 focus-visible:border-[#22D3EE]"
                data-testid="login-password"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-3" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-11 btn-sharp"
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Continue"}
              <ArrowRight size={14} weight="bold" className="ml-2" />
            </Button>
          </form>

          <div className="mt-8 text-sm text-neutral-600 leading-relaxed">
            Access is invite-only. If you received an invitation email, click the link in it to set up your account.
          </div>
        </div>
      </div>
    </div>
  );
}
